import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box, Card, CardContent, Typography, Button, Stack, CircularProgress, Divider, Alert,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { companySignatureApi } from "./massEmail/api/companySignatureApi";

/**
 * Admin-managed company/brand email signature. Used on every mass-email campaign
 * whose sender has not uploaded a personal signature of their own.
 */
export default function MassEmailCompanySignatureSection({ showSnackbar }) {
    const [currentUrl, setCurrentUrl] = useState(null); // server-stored signature URL
    const [localPreview, setLocalPreview] = useState(null); // selected-but-unsaved file
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);
    const inputRef = useRef(null);

    const notify = useCallback((msg, sev = "success") => {
        if (typeof showSnackbar === "function") showSnackbar(msg, sev);
    }, [showSnackbar]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await companySignatureApi.get();
            setCurrentUrl(data?.company_signature_url || null);
        } catch {
            notify("Failed to load the company signature.", "error");
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { load(); }, [load]);

    const pick = (event) => {
        const selected = event.target.files?.[0];
        if (!selected) return;
        setFile(selected);
        const reader = new FileReader();
        reader.onload = () => setLocalPreview(reader.result);
        reader.readAsDataURL(selected);
    };

    const handleUpload = async () => {
        if (!file) return;
        setSaving(true);
        try {
            const data = await companySignatureApi.upload(file);
            // Cache-bust so the freshly uploaded image is shown, not a stale one.
            const url = data?.company_signature_url
                ? `${data.company_signature_url}?t=${Date.now()}`
                : null;
            setCurrentUrl(url);
            setFile(null);
            setLocalPreview(null);
            if (inputRef.current) inputRef.current.value = "";
            notify("Company signature updated.");
        } catch (e) {
            const msg = e?.response?.status === 422
                ? "Please choose a valid image (PNG, JPG, GIF or WEBP, max 4MB)."
                : e?.response?.status === 403
                    ? "You don't have permission to change the company signature."
                    : "Failed to upload the company signature.";
            notify(msg, "error");
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        setRemoving(true);
        try {
            await companySignatureApi.remove();
            setCurrentUrl(null);
            setFile(null);
            setLocalPreview(null);
            if (inputRef.current) inputRef.current.value = "";
            notify("Company signature removed.");
        } catch {
            notify("Failed to remove the company signature.", "error");
        } finally {
            setRemoving(false);
        }
    };

    const shownImage = localPreview || currentUrl;

    return (
        <Card sx={{ borderRadius: 3, maxWidth: 720 }}>
            <CardContent>
                <Typography variant="h6" fontWeight={900}>Company Email Signature</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    This signature is attached to mass-email campaigns whenever the sending
                    agent has not uploaded a personal signature of their own. Use a clear
                    image (PNG or JPG), ideally around 320px wide.
                </Typography>

                <Divider sx={{ my: 2 }} />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                border: "1px solid rgba(0,0,0,0.09)",
                                borderRadius: "12px",
                                p: 2,
                                mb: 2,
                                bgcolor: "#fafafa",
                                minHeight: 120,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {shownImage ? (
                                <Box
                                    component="img"
                                    src={shownImage}
                                    alt="Company signature"
                                    sx={{ maxWidth: "100%", maxHeight: 160, display: "block" }}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No company signature set yet.
                                </Typography>
                            )}
                        </Box>

                        {!currentUrl && !localPreview ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Until a company signature is set, campaigns from agents without a
                                personal signature are sent without a signature image.
                            </Alert>
                        ) : null}

                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                            <input
                                ref={inputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/gif,image/webp"
                                onChange={pick}
                                style={{ display: "none" }}
                                id="company-signature-input"
                            />
                            <label htmlFor="company-signature-input">
                                <Button component="span" variant="outlined" startIcon={<UploadFileIcon />}>
                                    Choose image
                                </Button>
                            </label>

                            <Button
                                variant="contained"
                                onClick={handleUpload}
                                disabled={!file || saving}
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {saving ? "Uploading…" : "Save signature"}
                            </Button>

                            {currentUrl ? (
                                <Button
                                    variant="text"
                                    color="error"
                                    onClick={handleRemove}
                                    disabled={removing}
                                    startIcon={<DeleteOutlineIcon />}
                                >
                                    {removing ? "Removing…" : "Remove"}
                                </Button>
                            ) : null}
                        </Stack>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
